import React, { useEffect, useState, useCallback } from "react";
import { 
    collection, query, where, getDoc, getFirestore, doc, deleteDoc, updateDoc,
    getCountFromServer, getAggregateFromServer, sum 
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { 
    FiTrendingUp, FiAlertCircle, FiUsers, FiDollarSign, 
    FiCalendar, FiClock, FiTrash2, FiEdit2, FiX, FiSave, 
    FiChevronLeft, FiChevronRight, FiSearch
} from "react-icons/fi";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

// Хэрэв танайд AuthProvider байгаа бол ашиглаж болно
// import { useAuth } from "../../component/AuthProvider";

const MySwal = withReactContent(Swal);

export default function MonthlyReport() {
    // ⭐️ Зөвхөн "Жинхэнэ өр" (price > 0) бүхий машинуудыг хадгалах State
    const [reportData, setReportData] = useState([]); 
    
    const [monthlyStats, setMonthlyStats] = useState({ 
        totalIncome: 0, 
        residentCount: 0, 
        totalCars: 0,
        totalUnpaid: 0,
        unpaidCount: 0
    }); 
    
    const [loading, setLoading] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    
    const [selectedIds, setSelectedIds] = useState([]);
    const [isDeletingBulk, setIsDeletingBulk] = useState(false);
    
    const [searchInput, setSearchInput] = useState(""); 
    const [searchPlate, setSearchPlate] = useState("");

    // ⭐️ Локал хуудаслалт (Frontend дээр хийгдэнэ)
    const [currentPage, setCurrentPage] = useState(1); 
    const pageSize = 20; 
    
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [editFormData, setEditFormData] = useState({ car_plate: "", price: 0, payment: false });
    const [userRole, setUserRole] = useState("operator");
    
    const auth = getAuth();
    const db = getFirestore(auth.app);
    const functions = getFunctions(auth.app);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchPlate !== searchInput) {
                setSearchPlate(searchInput);
                setCurrentPage(1);
                setSelectedIds([]); 
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput, searchPlate]);

    const fetchUserRole = async () => {
        if (!auth.currentUser || !auth.currentUser.email) return;
        try {
            const userDocRef = doc(db, "parkings", auth.currentUser.displayName, "users", auth.currentUser.email); 
            const userSnap = await getDoc(userDocRef);
            if(userSnap.exists()){
                const data = userSnap.data();
                setUserRole(data['role']==="master" ? "master" : data['role'] === "admin" ? "admin" : "operator");
            }
        } catch (err) {}
    };

    // ⭐️ МАШИНЫ ЖАГСААЛТ БОЛОН СТАТИСТИКИЙГ НЭГТГЭЖ ТАТАХ
    const fetchMonthlyData = useCallback(async (forcedUser = null) => {
        const user = (forcedUser && forcedUser.uid) ? forcedUser : auth.currentUser;
        if (!user || !user.displayName) return;
        
        setLoading(true);
        const startOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
        const endOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0, 23, 59, 59);
        const recordsRef = collection(db, "parkings", user.displayName, "records");

        try {
            // 1. Статистик тоо баримтыг хамгийн хямд, хурдан аргаар бодох
            const baseQuery = query(recordsRef, where("entry_time", ">=", startOfMonth), where("entry_time", "<=", endOfMonth));
            const paidQuery = query(baseQuery, where("payment", "==", true));
            const residentQuery = query(baseQuery, where("price", "==", 0));

            const [totalCarsSnap, incomeSnap, residentSnap] = await Promise.all([
                getCountFromServer(baseQuery),
                getAggregateFromServer(paidQuery, { total: sum('price') }),
                getCountFromServer(residentQuery)
            ]);

            // 2. Серверээс "Төлөөгүй" бүх машиныг их хэмжээгээр (5000) татаж авах
            const searchLogs = httpsCallable(functions, 'searchParkingLogs');
            const response = await searchLogs({
                parkId: user.displayName,
                startTimeISO: startOfMonth.toISOString(),
                endTimeISO: endOfMonth.toISOString(),
                paymentFilter: "unpaid",
                searchPlate: searchPlate,
                limitSize: 5000 // Сарын бүх датаг багтаана
            });

            // 3. FRONT-END ШҮҮЛТ: Яг одоо зогсоолд байгаа (0₮) машинуудыг хасаж, ЖИНХЭНЭ ӨР-ийг үлдээх
            const actualDebts = response.data.logs
                .filter(log => log.price > 0) // 🔥 -0 гарах асуудлыг бүр мөсөн устгасан
                .map(log => ({
                    ...log,
                    entry_time: log.entry_time ? { toDate: () => new Date(log.entry_time) } : null,
                    exit_time: log.exit_time ? { toDate: () => new Date(log.exit_time) } : null
                }));

            // KPI картуудын утгыг шинэчлэх
            setMonthlyStats({
                totalCars: totalCarsSnap.data().count,
                totalIncome: incomeSnap.data().total || 0,
                residentCount: residentSnap.data().count,
                unpaidCount: actualDebts.length, // Шүүгдсэн жинхэнэ өртэй машинуудын тоо
                totalUnpaid: actualDebts.reduce((sum, r) => sum + r.price, 0)
            });

            setReportData(actualDebts);
            setCurrentPage(1);

        } catch (error) {
            console.error("Алдаа гарлаа:", error);
        } finally {
            setLoading(false);
        }
    }, [auth, selectedMonth, searchPlate, db, functions]);

    // Анхны ачаалалт
    useEffect(() => {
        const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
            if (user) {
                await user.getIdToken(true);
                fetchUserRole();
                fetchMonthlyData(user); 
            }
        });
        return () => unsubscribeAuth();
    }, []); 

    // Сар солих эсвэл хайлт хийх үед автоматаар ажиллана
    useEffect(() => {
        if (auth.currentUser && auth.currentUser.displayName) {
            fetchMonthlyData();
            setSelectedIds([]);
        }
    }, [selectedMonth, searchPlate]); 

    // Локал хуудаслалт бодох хэсэг
    const totalPages = Math.max(1, Math.ceil(reportData.length / pageSize));
    const paginatedLogs = reportData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const handleNext = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
    const handlePrev = () => setCurrentPage(prev => Math.max(prev - 1, 1));

    // Устгах, Засах үйлдлүүд
    const handleDeleteClick = (item) => {
        setSelectedItem(item);
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!selectedItem) return;
        try {
            await deleteDoc(doc(db, "parkings", auth.currentUser.displayName, "records", selectedItem.id));
            setDeleteModalOpen(false);
            fetchMonthlyData(); // Дахин шинэчилнэ
        } catch (error) {
            MySwal.fire({ icon: 'error', title: 'Алдаа', text: 'Устгаж чадсангүй.' });
        }
    };

    const handleEditClick = (item) => {
        setSelectedItem(item);
        setEditFormData({
            car_plate: item.car_plate,
            price: item.price,
            payment: item.payment || false
        });
        setEditModalOpen(true);
    };

    const handleSaveEdit = async () => {
        if (!selectedItem) return;
        try {
            const docRef = doc(db, "parkings", auth.currentUser.displayName, "records", selectedItem.id);
            await updateDoc(docRef, {
                car_plate: editFormData.car_plate,
                price: Number(editFormData.price),
                payment: editFormData.payment
            });
            setEditModalOpen(false);
            fetchMonthlyData(); 
        } catch (error) {
            MySwal.fire({ icon: 'error', title: 'Алдаа', text: 'Хадгалж чадсангүй.' });
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(reportData.map(item => item.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
    };

    const confirmBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        setIsDeletingBulk(true); 
        try {
            const deletePromises = selectedIds.map(id => 
                deleteDoc(doc(db, "parkings", auth.currentUser.displayName, "records", id))
            );
            await Promise.all(deletePromises);

            setSelectedIds([]); 
            setBulkDeleteModalOpen(false);
            fetchMonthlyData(); 
        } catch (error) {
            MySwal.fire({ icon: 'error', title: 'Алдаа', text: 'Олноор устгахад алдаа гарлаа.' });
        } finally {
            setIsDeletingBulk(false); 
        }
    };

  // ⭐️ САЙЖРУУЛСАН: Ямар ч төрлийн хугацаа (String, Date, Timestamp) ирсэн алдаа заахгүй ажиллана
    // ⭐️ ТӨГС ХУВИЛБАР: Firebase-ийн түүхий _seconds объектыг давхар танина
    const formatDate = (timestamp) => {
        if (!timestamp) return "-";
        
        // 1. Хэрэв .toDate() функцтэй байвал (Front-end хувиргалт)
        if (typeof timestamp.toDate === 'function') {
            return timestamp.toDate().toLocaleString('mn-MN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
        }
        
        // 2. 🌟 ШИНЭ: Серверээс хөрвүүлэгдэлгүй ирсэн Firebase Timestamp (_seconds) байвал
        if (timestamp && typeof timestamp === 'object' && timestamp._seconds) {
            return new Date(timestamp._seconds * 1000).toLocaleString('mn-MN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
        }
        
        // 3. Хэрэв шууд текст (ISO String) эсвэл энгийн Date объект ирсэн байвал
        const dateObj = new Date(timestamp);
        if (!isNaN(dateObj.getTime())) {
            return dateObj.toLocaleString('mn-MN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
        }
        
        return "-";
    };
    const isAllSelected = reportData.length > 0 && selectedIds.length === reportData.length;

    return (
        <div className="tw-bg-slate-50 tw-min-h-screen tw-font-sans tw-text-slate-800 tw-max-w-[100vw] tw-overflow-x-hidden">
            
            <header className="tw-bg-white tw-border-b tw-border-slate-200 tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-py-4 sm:tw-py-6">
                <div className="tw-flex tw-flex-col lg:tw-flex-row tw-justify-between tw-items-start lg:tw-items-center tw-gap-4">
                    <div className="tw-flex tw-items-center tw-gap-3 sm:tw-gap-4">
                        <div>
                            <h1 className="tw-text-xl sm:tw-text-2xl lg:tw-text-3xl tw-font-black tw-text-slate-900 tw-tracking-tight tw-m-0">Сарын Нэгдсэн Тайлан</h1>
                            <p className="tw-text-xs sm:tw-text-sm tw-font-medium tw-text-slate-500 tw-mt-1 tw-mb-0">{selectedMonth.getFullYear()} оны {selectedMonth.getMonth() + 1}-р сар</p>
                        </div>
                    </div>

                    <div className="tw-flex tw-items-center tw-w-full lg:tw-w-auto tw-bg-white tw-border tw-border-slate-200 tw-rounded-xl tw-shadow-sm tw-px-3 tw-py-2 hover:tw-border-blue-400 tw-transition-colors">
                        <FiCalendar className="tw-text-blue-500 tw-mr-2" size={20} />
                        <input 
                            type="month" 
                            className="tw-bg-transparent tw-w-full lg:tw-w-auto tw-text-sm tw-font-bold tw-text-slate-700 tw-outline-none tw-cursor-pointer"
                            value={`${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`}
                            onChange={(e) => setSelectedMonth(new Date(e.target.value))}
                        />
                    </div>
                </div>
            </header>

            <main className="tw-p-3 sm:tw-p-6 lg:tw-p-8 tw-py-6">
                {loading ? (
                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20 sm:tw-py-32">
                        <div className="tw-w-10 tw-h-10 tw-border-4 tw-border-blue-200 tw-border-t-blue-600 tw-rounded-full tw-animate-spin"></div>
                        <p className="tw-mt-4 tw-text-sm tw-font-bold tw-text-slate-500">Тайлангийн датаг нэгтгэж байна...</p>
                    </div>
                ) : (
                    <>
                        <section className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3 sm:tw-gap-6 tw-mb-6 sm:tw-mb-8">
                            <div className="tw-bg-white tw-rounded-2xl tw-p-4 sm:tw-p-6 tw-shadow-sm tw-border tw-border-slate-200 tw-relative tw-overflow-hidden">
                                <div className="tw-absolute tw-right-0 tw-top-0 tw-w-16 sm:tw-w-24 tw-h-16 sm:tw-h-24 tw-bg-emerald-500/10 tw-rounded-bl-[64px] tw-z-0"></div>
                                <div className="tw-relative tw-z-10 tw-flex tw-items-center tw-gap-3 sm:tw-gap-4">
                                    <div className="tw-w-10 sm:tw-w-12 tw-h-10 sm:tw-h-12 tw-rounded-xl tw-bg-emerald-50 tw-flex tw-items-center tw-justify-center tw-text-emerald-600 tw-flex-shrink-0">
                                        <FiDollarSign className="tw-w-5 tw-h-5 sm:tw-w-6 sm:tw-h-6" />
                                    </div>
                                    <div>
                                        <p className="tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-mb-1">Нийт Орлого</p>
                                        <h3 className="tw-text-sm sm:tw-text-2xl lg:tw-text-3xl tw-font-black tw-text-slate-900">{monthlyStats.totalIncome.toLocaleString()}₮</h3>
                                    </div>
                                </div>
                            </div>

                            <div className="tw-bg-white tw-rounded-2xl tw-p-4 sm:tw-p-6 tw-shadow-sm tw-border tw-border-slate-200 tw-border-l-4 tw-border-l-rose-500 tw-relative tw-overflow-hidden">
                                <div className="tw-absolute tw-right-0 tw-top-0 tw-w-16 sm:tw-w-24 tw-h-16 sm:tw-h-24 tw-bg-rose-500/10 tw-rounded-bl-[64px] tw-z-0"></div>
                                <div className="tw-relative tw-z-10 tw-flex tw-items-center tw-gap-3 sm:tw-gap-4">
                                    <div className="tw-w-10 sm:tw-w-12 tw-h-10 sm:tw-h-12 tw-rounded-xl tw-bg-rose-50 tw-flex tw-items-center tw-justify-center tw-text-rose-600 tw-flex-shrink-0">
                                        <FiAlertCircle className="tw-w-5 tw-h-5 sm:tw-w-6 sm:tw-h-6" />
                                    </div>
                                    <div>
                                        <p className="tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-mb-1">Төлөөгүй ({monthlyStats.unpaidCount})</p>
                                        <h3 className="tw-text-sm sm:tw-text-2xl lg:tw-text-3xl tw-font-black tw-text-rose-600">{monthlyStats.totalUnpaid.toLocaleString()}₮</h3>
                                    </div>
                                </div>
                            </div>

                            <div className="tw-bg-white tw-rounded-2xl tw-p-4 sm:tw-p-6 tw-shadow-sm tw-border tw-border-slate-200 tw-relative tw-overflow-hidden">
                                <div className="tw-absolute tw-right-0 tw-top-0 tw-w-16 sm:tw-w-24 tw-h-16 sm:tw-h-24 tw-bg-blue-500/10 tw-rounded-bl-[64px] tw-z-0"></div>
                                <div className="tw-relative tw-z-10 tw-flex tw-items-center tw-gap-3 sm:tw-gap-4">
                                    <div className="tw-w-10 sm:tw-w-12 tw-h-10 sm:tw-h-12 tw-rounded-xl tw-bg-blue-50 tw-flex tw-items-center tw-justify-center tw-text-blue-600 tw-flex-shrink-0">
                                        <FiUsers className="tw-w-5 tw-h-5 sm:tw-w-6 sm:tw-h-6" />
                                    </div>
                                    <div>
                                        <p className="tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-mb-1">Оршин суугч</p>
                                        <h3 className="tw-text-sm sm:tw-text-2xl lg:tw-text-3xl tw-font-black tw-text-slate-900">{monthlyStats.residentCount}</h3>
                                    </div>
                                </div>
                            </div>

                            <div className="tw-bg-white tw-rounded-2xl tw-p-4 sm:tw-p-6 tw-shadow-sm tw-border tw-border-slate-200 tw-relative tw-overflow-hidden">
                                <div className="tw-absolute tw-right-0 tw-top-0 tw-w-16 sm:tw-w-24 tw-h-16 sm:tw-h-24 tw-bg-indigo-500/10 tw-rounded-bl-[64px] tw-z-0"></div>
                                <div className="tw-relative tw-z-10 tw-flex tw-items-center tw-gap-3 sm:tw-gap-4">
                                    <div className="tw-w-10 sm:tw-w-12 tw-h-10 sm:tw-h-12 tw-rounded-xl tw-bg-indigo-50 tw-flex tw-items-center tw-justify-center tw-text-indigo-600 tw-flex-shrink-0">
                                        <FiTrendingUp className="tw-w-5 tw-h-5 sm:tw-w-6 sm:tw-h-6" />
                                    </div>
                                    <div>
                                        <p className="tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-mb-1">Нийт Нэвтрэлт</p>
                                        <h3 className="tw-text-sm sm:tw-text-2xl lg:tw-text-3xl tw-font-black tw-text-slate-900">{monthlyStats.totalCars}</h3>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="tw-bg-white tw-shadow-sm tw-border tw-border-slate-200 tw-rounded-2xl tw-w-full tw-overflow-hidden">
                            
                            <div className="tw-px-4 sm:tw-px-6 tw-py-4 sm:tw-py-5 tw-border-b tw-border-slate-200 tw-bg-white tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-stretch md:tw-items-center tw-gap-4">
                                <h3 className="tw-text-base sm:tw-text-lg tw-font-bold tw-text-slate-800 tw-flex tw-items-center tw-gap-2 tw-whitespace-nowrap tw-m-0">
                                    <FiClock className="tw-text-rose-500"/> Төлбөр төлөөгүй машинууд
                                </h3>
                                
                                <div className="tw-flex tw-flex-col sm:tw-flex-row tw-w-full md:tw-w-auto tw-items-stretch sm:tw-items-center tw-gap-3">
                                    <div className="tw-relative tw-w-full sm:tw-w-64">
                                        <FiSearch className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-text-slate-400" size={16} />
                                        <input 
                                            type="text"
                                            placeholder="Улсын дугаараар хайх..."
                                            value={searchInput}
                                            onChange={(e) => setSearchInput(e.target.value)}
                                            className="tw-w-full tw-pl-9 tw-pr-3 tw-py-2 sm:tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-lg tw-text-sm tw-font-medium tw-text-slate-800 focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 tw-transition-all"
                                        />
                                    </div>

                                    {selectedIds.length > 0 && (
                                        <button 
                                            onClick={() => setBulkDeleteModalOpen(true)}
                                            className="tw-w-full sm:tw-w-auto tw-flex tw-items-center tw-justify-center tw-gap-2 tw-bg-rose-50 tw-text-rose-600 tw-border tw-border-rose-200 hover:tw-bg-rose-100 tw-px-4 tw-py-2 sm:tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-bold tw-transition-colors tw-whitespace-nowrap"
                                        >
                                            <FiTrash2 /> ({selectedIds.length}) устгах
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="tw-overflow-x-auto tw-w-full tw-block">
                                {reportData.length > 0 ? (
                                    <table className="tw-w-full tw-text-left tw-border-collapse tw-min-w-[700px] md:tw-min-w-[800px]">
                                        <thead className="tw-bg-slate-50 tw-border-b tw-border-slate-200">
                                            <tr> 
                                                <th className="tw-px-4 sm:tw-px-6 tw-py-3 sm:tw-py-4 tw-w-12 sm:tw-w-16">
                                                    <input 
                                                        type="checkbox" 
                                                        checked={isAllSelected}
                                                        onChange={handleSelectAll}
                                                        className="tw-w-4 tw-h-4 tw-rounded tw-border-slate-300 tw-text-blue-600 focus:tw-ring-blue-500 tw-cursor-pointer"
                                                    />
                                                </th>
                                                <th className="tw-px-3 sm:tw-px-4 tw-py-3 sm:tw-py-4 tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider">Дугаар</th>
                                                <th className="tw-px-3 sm:tw-px-4 tw-py-3 sm:tw-py-4 tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider">Орсон</th>
                                                <th className="tw-px-3 sm:tw-px-4 tw-py-3 sm:tw-py-4 tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider">Гарсан</th>
                                                <th className="tw-px-3 sm:tw-px-4 tw-py-3 sm:tw-py-4 tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider">Хугацаа</th>
                                                <th className="tw-px-3 sm:tw-px-4 tw-py-3 sm:tw-py-4 tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-text-center">Төлөх Дүн</th>
                                                <th className="tw-px-4 sm:tw-px-6 tw-py-3 sm:tw-py-4 tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-text-right">Үйлдэл</th>
                                            </tr>
                                        </thead>
                                        <tbody className="tw-divide-y tw-divide-slate-100">
                                            {paginatedLogs.map(r => {
                                                const isSelected = selectedIds.includes(r.id);
                                                return (
                                                    <tr key={r.id} className={`tw-transition-colors hover:tw-bg-slate-50/80 ${isSelected ? 'tw-bg-blue-50/30' : ''}`}>
                                                        <td className="tw-px-4 sm:tw-px-6 tw-py-3 sm:tw-py-4">
                                                            <input 
                                                                type="checkbox" 
                                                                checked={isSelected}
                                                                onChange={() => handleSelectOne(r.id)}
                                                                className="tw-w-4 tw-h-4 tw-rounded tw-border-slate-300 tw-text-blue-600 focus:tw-ring-blue-500 tw-cursor-pointer"
                                                            />
                                                        </td>
                                                        <td className="tw-px-3 sm:tw-px-4 tw-py-3 sm:tw-py-4">
                                                            <span className="tw-inline-block tw-px-2 sm:tw-px-2.5 tw-py-1 tw-bg-slate-100 tw-border tw-border-slate-200 tw-rounded-md tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-800 tw-tracking-wider">
                                                                {r.car_plate}
                                                            </span>
                                                        </td>
                                                        <td className="tw-px-3 sm:tw-px-4 tw-py-3 sm:tw-py-4 tw-text-xs sm:tw-text-sm tw-text-slate-600">{formatDate(r.entry_time)}</td>
                                                        <td className="tw-px-3 sm:tw-px-4 tw-py-3 sm:tw-py-4 tw-text-xs sm:tw-text-sm tw-text-slate-600">{formatDate(r.exit_try_time) || "Гараагүй"}</td>
                                                        <td className="tw-px-3 sm:tw-px-4 tw-py-3 sm:tw-py-4 tw-text-xs sm:tw-text-sm tw-font-medium tw-text-slate-700">{r.duration || "-"}</td>
                                                        
                                                        {/* 🔥 -0 гарахгүй, 0₮ гарвал саарал өнгөтэй харагдах хамгаалалт нэмсэн */}
                                                        <td className="tw-px-3 sm:tw-px-4 tw-py-3 sm:tw-py-4 tw-text-center tw-text-xs sm:tw-text-sm tw-font-bold tw-text-rose-600">
                                                            {r.price > 0 ? `-${Number(r.price).toLocaleString()}₮` : <span className="tw-text-slate-400">0₮</span>}
                                                        </td>

                                                        <td className="tw-px-4 sm:tw-px-6 tw-py-3 sm:tw-py-4 tw-text-right">
                                                            <div className="tw-flex tw-justify-end tw-gap-1 sm:tw-gap-2">
                                                                <button onClick={() => handleEditClick(r)} className="tw-p-1.5 sm:tw-p-2 tw-text-slate-400 hover:tw-text-blue-600 hover:tw-bg-blue-50 tw-rounded-lg tw-transition-colors"><FiEdit2 size={16}/></button>
                                                                <button onClick={() => handleDeleteClick(r)} className="tw-p-1.5 sm:tw-p-2 tw-text-slate-400 hover:tw-text-rose-600 hover:tw-bg-rose-50 tw-rounded-lg tw-transition-colors"><FiTrash2 size={16}/></button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16 sm:tw-py-20 tw-text-slate-400">
                                        <div className="tw-w-12 sm:tw-w-16 tw-h-12 sm:tw-h-16 tw-bg-emerald-50 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-3 sm:tw-mb-4 tw-text-emerald-500">
                                            <svg className="tw-w-6 sm:tw-w-8 tw-h-6 sm:tw-h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                                        </div>
                                        <p className="tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-600">Энэ сард төлөгдөөгүй эсвэл хайлтад тохирох машин алга байна.</p>
                                    </div>
                                )}
                            </div>

                            {reportData.length > 0 && (
                                <div className="tw-px-4 sm:tw-px-6 tw-py-4 tw-border-t tw-border-slate-200 tw-bg-slate-50 tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-justify-between tw-gap-4">
                                    <div className="tw-text-xs sm:tw-text-sm tw-text-slate-500">
                                        Нийт <span className="tw-font-bold tw-text-slate-900">{reportData.length}</span> бичлэгээс 
                                        <span className="tw-font-bold tw-text-slate-900"> {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, reportData.length)} </span> 
                                        харагдаж байна
                                    </div>
                                    <div className="tw-flex tw-items-center tw-gap-2">
                                        <button 
                                            onClick={handlePrev} 
                                            disabled={currentPage === 1} 
                                            className="tw-p-2 tw-rounded-lg tw-bg-white tw-border tw-border-slate-200 tw-text-slate-600 hover:tw-bg-slate-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
                                        >
                                            <FiChevronLeft size={20} />
                                        </button>
                                        
                                        <div className="tw-px-3 sm:tw-px-4 tw-py-2 tw-rounded-lg tw-bg-white tw-border tw-border-slate-200 tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-700">
                                            {currentPage} / {totalPages}
                                        </div>

                                        <button 
                                            onClick={handleNext} 
                                            disabled={currentPage === totalPages} 
                                            className="tw-p-2 tw-rounded-lg tw-bg-white tw-border tw-border-slate-200 tw-text-slate-600 hover:tw-bg-slate-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
                                        >
                                            <FiChevronRight size={20} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </section>
                    </>
                )}
            </main>

            {/* --- MODALS --- */}
            {bulkDeleteModalOpen && (
                <div className="tw-fixed tw-inset-0 tw-z-[1000] tw-bg-slate-900/60 tw-backdrop-blur-sm tw-flex tw-items-center tw-justify-center tw-p-4 tw-animate-in tw-fade-in">
                    <div className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-w-full tw-max-w-[90vw] sm:tw-max-w-md tw-overflow-hidden tw-animate-in tw-zoom-in-95">
                        <div className="tw-px-4 sm:tw-px-6 tw-py-4 sm:tw-py-5 tw-border-b tw-border-slate-100 tw-flex tw-justify-between tw-items-center">
                            <h3 className="tw-text-base sm:tw-text-lg tw-font-bold tw-text-rose-600">Олноор устгах уу?</h3>
                            <button disabled={isDeletingBulk} onClick={() => setBulkDeleteModalOpen(false)} className="tw-text-slate-400 hover:tw-text-rose-500 tw-transition-colors disabled:tw-opacity-50"><FiX size={24}/></button>
                        </div>
                        <div className="tw-p-4 sm:tw-p-6">
                            <p className="tw-text-sm sm:tw-text-base tw-text-slate-600">Та сонгосон <b className="tw-text-slate-900">{selectedIds.length}</b> машины бүртгэлийг устгахдаа итгэлтэй байна уу? Энэ үйлдлийг буцаах боломжгүй.</p>
                        </div>
                        <div className="tw-px-4 sm:tw-px-6 tw-py-4 tw-bg-slate-50 tw-border-t tw-border-slate-100 tw-flex tw-justify-end tw-gap-3">
                            <button disabled={isDeletingBulk} onClick={() => setBulkDeleteModalOpen(false)} className="tw-px-4 sm:tw-px-5 tw-py-2 sm:tw-py-2.5 tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-600 hover:tw-bg-slate-200 tw-rounded-xl tw-transition-colors disabled:tw-opacity-50">Болих</button>
                            <button disabled={isDeletingBulk} onClick={confirmBulkDelete} className="tw-flex tw-items-center tw-gap-2 tw-px-4 sm:tw-px-6 tw-py-2 sm:tw-py-2.5 tw-text-xs sm:tw-text-sm tw-font-bold tw-text-white tw-bg-rose-500 hover:tw-bg-rose-600 tw-rounded-xl tw-shadow-sm tw-transition-colors disabled:tw-bg-rose-300">
                                {isDeletingBulk ? "Устгаж байна..." : <><FiTrash2/> Бүгдийг устгах</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {deleteModalOpen && (
                <div className="tw-fixed tw-inset-0 tw-z-[1000] tw-bg-slate-900/60 tw-backdrop-blur-sm tw-flex tw-items-center tw-justify-center tw-p-4 tw-animate-in tw-fade-in">
                    <div className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-w-full tw-max-w-[90vw] sm:tw-max-w-md tw-overflow-hidden tw-animate-in tw-zoom-in-95">
                        <div className="tw-px-4 sm:tw-px-6 tw-py-4 sm:tw-py-5 tw-border-b tw-border-slate-100 tw-flex tw-justify-between tw-items-center">
                            <h3 className="tw-text-base sm:tw-text-lg tw-font-bold tw-text-rose-600">Устгах уу?</h3>
                            <button onClick={() => setDeleteModalOpen(false)} className="tw-text-slate-400 hover:tw-text-rose-500 tw-transition-colors"><FiX size={24}/></button>
                        </div>
                        <div className="tw-p-4 sm:tw-p-6">
                            <p className="tw-text-sm sm:tw-text-base tw-text-slate-600">Та <b className="tw-text-slate-900">{selectedItem?.car_plate}</b> машины бүртгэлийг устгахдаа итгэлтэй байна уу?</p>
                        </div>
                        <div className="tw-px-4 sm:tw-px-6 tw-py-4 tw-bg-slate-50 tw-border-t tw-border-slate-100 tw-flex tw-justify-end tw-gap-3">
                            <button onClick={() => setDeleteModalOpen(false)} className="tw-px-4 sm:tw-px-5 tw-py-2 sm:tw-py-2.5 tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-600 hover:tw-bg-slate-200 tw-rounded-xl tw-transition-colors">Болих</button>
                            <button onClick={confirmDelete} className="tw-flex tw-items-center tw-gap-2 tw-px-4 sm:tw-px-6 tw-py-2 sm:tw-py-2.5 tw-text-xs sm:tw-text-sm tw-font-bold tw-text-white tw-bg-rose-500 hover:tw-bg-rose-600 tw-rounded-xl tw-shadow-sm tw-transition-colors"><FiTrash2/> Устгах</button>
                        </div>
                    </div>
                </div>
            )}

            {editModalOpen && (
                <div className="tw-fixed tw-inset-0 tw-z-[1000] tw-bg-slate-900/60 tw-backdrop-blur-sm tw-flex tw-items-center tw-justify-center tw-p-4 tw-animate-in tw-fade-in">
                    <div className="tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-w-full tw-max-w-[90vw] sm:tw-max-w-md tw-overflow-hidden tw-animate-in tw-zoom-in-95">
                        <div className="tw-px-4 sm:tw-px-6 tw-py-4 sm:tw-py-5 tw-border-b tw-border-slate-100 tw-flex tw-justify-between tw-items-center">
                            <h3 className="tw-text-base sm:tw-text-lg tw-font-bold tw-text-slate-800">Засах</h3>
                            <button onClick={() => setEditModalOpen(false)} className="tw-text-slate-400 hover:tw-text-rose-500 tw-transition-colors"><FiX size={24}/></button>
                        </div>
                        <div className="tw-p-4 sm:tw-p-6 tw-space-y-4">
                            <div>
                                <label className="tw-block tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-mb-1.5">Улсын дугаар</label>
                                <input 
                                    type="text" 
                                    value={editFormData.car_plate}
                                    onChange={(e) => setEditFormData({...editFormData, car_plate: e.target.value})}
                                    className="tw-w-full tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-xs sm:tw-text-sm tw-font-bold tw-tracking-widest focus:tw-bg-white focus:tw-border-blue-500 focus:tw-outline-none"
                                />
                            </div>
                            <div>
                                <label className="tw-block tw-text-[10px] sm:tw-text-xs tw-font-bold tw-text-slate-500 tw-uppercase tw-mb-1.5">Төлбөр (₮)</label>
                                <input 
                                    type="number" 
                                    value={editFormData.price}
                                    onChange={(e) => setEditFormData({...editFormData, price: e.target.value})}
                                    className="tw-w-full tw-px-3 sm:tw-px-4 tw-py-2 sm:tw-py-2.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-xl tw-text-xs sm:tw-text-sm tw-font-bold focus:tw-bg-white focus:tw-border-blue-500 focus:tw-outline-none"
                                />
                            </div>
                            <div className="tw-pt-2">
                                <label className="tw-flex tw-items-center tw-gap-3 tw-cursor-pointer">
                                    <input 
                                        type="checkbox"
                                        checked={editFormData.payment}
                                        onChange={(e) => setEditFormData({...editFormData, payment: e.target.checked})}
                                        className="tw-w-4 sm:tw-w-5 tw-h-4 sm:tw-h-5 tw-rounded tw-border-slate-300 tw-text-blue-600 focus:tw-ring-blue-500 tw-cursor-pointer"
                                    />
                                    <span className="tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-700">Төлбөр төлөгдсөн гэж тэмдэглэх</span>
                                </label>
                                <p className="tw-text-[10px] sm:tw-text-xs tw-text-slate-500 tw-mt-1 tw-pl-7 sm:tw-pl-8">Хэрэв үүнийг чагтлавал энэ машин төлөгдөөгүй жагсаалтаас хасагдана.</p>
                            </div>
                        </div>
                        <div className="tw-px-4 sm:tw-px-6 tw-py-4 tw-bg-slate-50 tw-border-t tw-border-slate-100 tw-flex tw-justify-end tw-gap-3">
                            <button onClick={() => setEditModalOpen(false)} className="tw-px-4 sm:tw-px-5 tw-py-2 sm:tw-py-2.5 tw-text-xs sm:tw-text-sm tw-font-bold tw-text-slate-600 hover:tw-bg-slate-200 tw-rounded-xl tw-transition-colors">Болих</button>
                            <button onClick={handleSaveEdit} className="tw-flex tw-items-center tw-gap-2 tw-px-4 sm:tw-px-6 tw-py-2 sm:tw-py-2.5 tw-text-xs sm:tw-text-sm tw-font-bold tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-xl tw-shadow-sm tw-transition-colors"><FiSave/> Хадгалах</button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}